import { Icon, Loader } from '@insio/ui-toolkit';

export interface FormatCardProps {
  /** Format identifier used as the React key and aria-label base. */
  icon: 'file-signature' | 'file-invoice-dollar' | 'file-lines' | 'grid-2';
  title: string;
  description: string;
  /** Whether this card is currently triggering an export. */
  isLoading: boolean;
  /** Whether this card (or the whole modal) is disabled. */
  isDisabled: boolean;
  /** Label shown inline next to the title while loading. */
  exportingLabel?: string;
  onClick: () => void;
}

/**
 * Clickable card representing a single export format option.
 * Displays an icon (or spinner while loading), title, description, and a trailing arrow.
 */
export function FormatCard({
  icon,
  title,
  description,
  isLoading,
  isDisabled,
  exportingLabel,
  onClick
}: FormatCardProps) {
  return (
    <button
      type='button'
      disabled={isDisabled}
      onClick={onClick}
      className={[
        'w-full flex items-center gap-md p-md rounded-lg border border-border-base text-left transition-all',
        isLoading
          ? 'bg-primary-base/5 shadow-md cursor-wait'
          : isDisabled
            ? 'bg-background-input opacity-50 cursor-not-allowed'
            : 'bg-background-input hover:bg-primary-base/5 hover:shadow-md cursor-pointer'
      ].join(' ')}
      aria-label={title}
    >
      {/* Icon / spinner */}
      <div className='shrink-0 w-10 h-10 flex items-center justify-center rounded-md bg-background-muted'>
        {isLoading ? (
          <Loader size='sm' />
        ) : (
          <Icon
            name={icon}
            className={isDisabled ? 'text-foreground-muted' : 'text-primary-base'}
          />
        )}
      </div>

      {/* Label */}
      <div className='flex-1 min-w-0'>
        <p
          className={`text-sm font-semibold ${isDisabled ? 'text-foreground-muted' : 'text-foreground-base'}`}
        >
          {title}
          {isLoading && exportingLabel && (
            <span className='ml-sm text-xs font-normal text-foreground-muted'>
              {exportingLabel}
            </span>
          )}
        </p>
        <p className='text-xs text-foreground-muted mt-2xs'>{description}</p>
      </div>

      {/* Arrow */}
      {!isLoading && !isDisabled && (
        <Icon name='arrow-right' className='shrink-0 text-foreground-muted' aria-hidden='true' />
      )}
    </button>
  );
}
